import _ from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getBillingContractBillsAsync, getMemberAssignContractsAsync } from "src/api/contract/contract-api";
import { BillingContractBillModel, ContractStep, MemberContractModel, getContractStepText } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { usePartnerNavigate } from "src/api/hooks/usePartnerNavigate";
import { BaseBottomSheet } from "src/components";
import Header from "src/components/layout/Header";
import MetaTag from "src/components/layout/MetaTag";
import { getMypageTitle } from "src/utils/app-Info";
import { useMediaQueries } from "src/pages/hooks/media-query";
import BillItem from "./components/BillItem";

// 계약상태 별 status 태그 클래스
const getContractStepStatusClass = (contractStep?: ContractStep) => {
  switch (contractStep) {
    case "USE_PROGRESS": // 이용중
      return "F";
    case "USE_APPROVAL": // 이용승인
      return "E";
    case "TERMINATE_RECEIVED": // 해지접수
      return "G";
    default: // 계약체결, 이용완료, 해지완료
      return "H";
  }
};

/**
 * 마이페이지 메인 > 신청/계약 내역 > 상세 > 청구 내역 목록 화면
 */
const ContractBillList = () => {
  const { isMobile } = useMediaQueries();

  const initializedRef = useRef(false);

  const { t } = useTranslation(["bill", "common"]);

  const navigate = usePartnerNavigate();

  // path variable 계약 id
  const { contractId } = useParams();

  // 로그인 사용자 계약 목록 조회 api
  const { executeAsync: getMemberAssignContracts } = useApiOperation(getMemberAssignContractsAsync);

  // 청구정보 결제현황 조회 api
  const { executeAsync: getBillingContractBills } = useApiOperation(getBillingContractBillsAsync);

  // 로그인 사용자 계약 목록 (셀렉트 박스 목록)
  const [contracts, setContracts] = useState<MemberContractModel[]>([]);

  // 선택되어있는 계약
  const [selectedContract, setSelectedContract] = useState<MemberContractModel>();

  // 청구정보 결제현황
  const [billingContractBill, setBillingContractBill] = useState<BillingContractBillModel>();

  // 계약 선택 바텀시트 오픈여부
  const [isOpenBottomSheet, setIsOpenBottomSheet] = useState<boolean>(false);
  // 계약 선택 셀렉트박스 오픈여부
  const [isOpenDrop, setIsOpenDrop] = useState<boolean>(false);

  // 표시 대상 결제상태(PAYMENT_READY / PAYMENT_SUCCESS / PAYMENT_FREE)인 청구내역 목록
  // 원청구/연체청구 모두 originBill.billPayStatus 를 기준으로 동일하게 필터링
  // TODO: 연체청구도 동일 기준으로 필터링되는 것이 맞는지 백엔드 확인 필요
  const billList = billingContractBill?.billList?.filter((bill) =>
    ["PAYMENT_READY", "PAYMENT_SUCCESS", "PAYMENT_FREE"].includes(bill.originBill.billPayStatus || ""),
  );

  // 로그인 사용자 계약 목록 조회
  const fetchMemberAssignContracts = useCallback(async () => {
    const { data } = await getMemberAssignContracts({
      isContractor: true,
      contractSteps: "CONTRACT_ACCEPT,USE_APPROVAL,USE_PROGRESS,USE_COMPLETE,TERMINATE_RECEIVED,TERMINATE_COMPLETE",
    });
    const _contracts = (data?.data?.content || [])
      // 마이페이지 메인 > 신청/계약 내역 > 상세 화면 > 나의 납부 상태 노출 조건과 동일
      // F/O 타입 이고, 계약체결 / 이용승인 / 이용중 / 이용완료 / 해지접수 / 해지완료
      .filter(
        (_contract: MemberContractModel) =>
          _contract?.spaceProductType !== "TIME_COURT" &&
          ["CONTRACT_ACCEPT", "USE_APPROVAL", "USE_PROGRESS", "USE_COMPLETE", "TERMINATE_RECEIVED", "TERMINATE_COMPLETE"].includes(
            _contract?.contractStep || "",
          ),
      )
      .sort((a: MemberContractModel, b: MemberContractModel) => Number(b?.contractId || 0) - Number(a?.contractId || 0));

    setContracts(_contracts);
  }, [getMemberAssignContracts]);

  // 청구정보 결제현황 조회
  const fetchBillingContractBills = useCallback(
    async (contractId: number | string) => {
      const { data } = await getBillingContractBills({ contractId });

      // const _temp = data?.data.billList!.filter((bm: BillModel) => bm.originBill.billPayStatus !== "TEMP_SAVE");
      const _temp = data?.data.billList!;
      data.data.billList = _.sortBy(_temp, ["originBill.billIssueDate"]).reverse();

      setBillingContractBill(data.data);

      if (!initializedRef.current) {
        initializedRef.current = true;
      }
    },
    [getBillingContractBills],
  );
  useEffect(() => {
    // 로그인 사용자 계약 목록 조회
    fetchMemberAssignContracts();
  }, [fetchMemberAssignContracts]);

  // URL 의 contractId 와 selectedContract 동기화 (뒤로가기 등 외부 URL 변경 대응)
  useEffect(() => {
    if (!contractId || contracts.length === 0) return;
    if (String(selectedContract?.contractId) === contractId) return;
    const _selectedContract = contracts.find((c) => String(c.contractId) === contractId);
    if (_selectedContract) {
      setSelectedContract(_selectedContract);
    }
  }, [contractId, contracts, selectedContract]);

  useEffect(() => {
    // 계약 선택을 바꿀때마다
    if (selectedContract && selectedContract?.contractId) {
      // 청구정보 결제현황 조회
      fetchBillingContractBills(selectedContract.contractId);
    }
  }, [selectedContract, fetchBillingContractBills]);

  return (
    <>
      <MetaTag title={getMypageTitle()} />
      <Header
        headerType="BACK"
        title={t("billingHistory.title")}
        onClickBackButton={() => {
          navigate(-1);
        }}
      />

      <div className="contract-page">
        <h1 className="base-title">{t("billingHistory.title")}</h1>
        {isMobile ? (
          <div className="contract-select-building">
            {selectedContract && (
              <button
                className={`chevron-down-btn text-left ${isOpenBottomSheet ? "--active" : ""}`}
                onClick={() => {
                  setIsOpenBottomSheet(true);
                }}
              >
                <div className="d-flex flex-center gap-10">
                  <span className={`status ${getContractStepStatusClass(selectedContract?.contractStep)}`}>
                    {getContractStepText(selectedContract?.contractStep)}
                  </span>
                  <p className="ellipsis">{selectedContract?.contractName || "-"}</p>
                </div>
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="contract-select-building">
              {selectedContract && (
                <button
                  className={`chevron-down-btn text-left ${isOpenDrop ? "--active" : ""}`}
                  onClick={() => {
                    setIsOpenDrop((isOpenDrop) => !isOpenDrop);
                  }}
                >
                  <div className="d-flex flex-center gap-10">
                    <span className={`status ${getContractStepStatusClass(selectedContract?.contractStep)}`}>
                      {getContractStepText(selectedContract?.contractStep)}
                    </span>
                    <p className="ellipsis">{selectedContract?.contractName || "-"}</p>
                  </div>
                </button>
              )}

              {isOpenDrop && (
                <div className="contract-select-building__dropdown">
                  <h2>{t("billingHistory.selectContractPrompt")}</h2>
                  <ul className="building-list">
                    {contracts.map((contract: MemberContractModel, index: number) => (
                      <div
                        key={index}
                        className="building-list__item"
                        onClick={() => {
                          setSelectedContract(contract);
                          setIsOpenDrop(false);
                          navigate(`/court/mypage/contracts/${contract.contractId}/bills`);
                        }}
                      >
                        <div className="building-list__item-wrap">
                          <div className="building-list__item-info">
                            <div className="d-flex flex-center gap-10">
                              <span className={`status ${getContractStepStatusClass(contract?.contractStep)}`}>
                                {getContractStepText(contract?.contractStep)}
                              </span>
                              <p>{contract?.contractName || "-"}</p>
                            </div>
                            <p className="mt10 text-primary3">({contract?.contractApplyNumber})</p>
                          </div>
                          {selectedContract?.contractId === contract.contractId ? (
                            <div className="building-list__item-checked"></div>
                          ) : (
                            <div className="building-list__item-default"></div>
                          )}
                        </div>
                      </div>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </>
        )}

        {initializedRef.current && (
          <article className="contract-history-confirmation">
            {billList?.length === 0 ? (
              <section className="contents-empty">
                <p>{t("billingHistory.emptyMessage")}</p>
              </section>
            ) : (
              <section className="base-section-layout">
                <div className="base-section-layout__container gap20 border-bottom-gray">
                  {billList?.map((billItem, index: number) => (
                    <BillItem key={index} selectedContract={selectedContract} billItem={billItem} />
                  ))}
                </div>
              </section>
            )}
          </article>
        )}

        {isMobile && (
          <BaseBottomSheet
            isOpen={isOpenBottomSheet}
            onClose={() => setIsOpenBottomSheet(false)}
            title={t("billingHistory.selectContractPrompt")}
            className="scrollable"
          >
            <div className="contract-select-sheet">
              <div className="building-list">
                {contracts.map((contract: MemberContractModel, index: number) => (
                  <div
                    key={index}
                    className="building-list__item"
                    onClick={() => {
                      setSelectedContract(contract);
                      setIsOpenBottomSheet(false);
                      navigate(`/court/mypage/contracts/${contract.contractId}/bills`);
                    }}
                  >
                    <div className="building-list__item-wrap">
                      <div className="building-list__item-info">
                        <div className="d-flex flex-center gap-10">
                          <span className={`status ${getContractStepStatusClass(contract?.contractStep)}`}>
                            {getContractStepText(contract?.contractStep)}
                          </span>
                          <p>{contract?.contractName || "-"}</p>
                        </div>
                        <p className="mt10 text-primary3">({contract?.contractApplyNumber})</p>
                      </div>
                      {selectedContract?.contractId === contract.contractId && <div className="building-list__item-checked"></div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </BaseBottomSheet>
        )}
      </div>
    </>
  );
};

export default ContractBillList;
